export class DetailsTransition extends HTMLDetailsElement {
	public static NAME = "details-transition";

	#summary: HTMLElement;
	#content: HTMLElement;
	#isOpen: boolean;
	#isDropdown: boolean;
	openStart: Event;
	openEnd: Event;
	closeStart: Event;
	closeEnd: Event;
	#useHeight: boolean;
	name: string;

	get open(): boolean {
		return this.#isOpen;
	}

	set open(value: boolean) {
		this.toggle(value);
	}

	toggle = (open?: boolean): void => {
		if (open === this.#isOpen) {
			return;
		}

		if (this.#useHeight) {
			this.#content.style.height = `${
				this.#isOpen ? this.#content.offsetHeight : 0
			}px`;
			this.#content.style.overflow = "hidden";
		}

		this.toggleAttribute("closing", !this.#isOpen);

		if (this.#isOpen) {
			this.dispatchEvent(this.closeStart);
			this.#isOpen = false;
			if (this.#isDropdown) {
				document.removeEventListener("click", this.#handleClickOutside);
				document.removeEventListener("keydown", this.#handleKeyDown);
			}
		} else {
			this.#isOpen = true;
			this.toggleAttribute("open", true);
			this.dispatchEvent(this.openStart);
			if (this.#isDropdown) {
				document.addEventListener("click", this.#handleClickOutside);
				document.addEventListener("keydown", this.#handleKeyDown);
			}
		}

		void this.#content.offsetHeight; // Checking offsetHeight forces paint allowing for transition.

		if (this.#useHeight) {
			this.#content.style.height = `${
				this.#isOpen ? this.#content.scrollHeight : 0
			}px`;
		}
		this.toggleAttribute("closing", !this.#isOpen);

		const animationPromises = Promise.all(
			this.#content
				.getAnimations({
					//subtree: true,
				})
				// .filter(
				// 	(animation) => !(animation as CSSAnimation).animationName,
				// )
				.map((animation) => {
					if (
						(animation as CSSTransition).transitionProperty ===
						"height"
					) {
						animation.finished
							.then(() => {
								if (this.#isOpen) {
									this.#content.style.height = "";
									this.#content.style.overflow = "";
								}
							})
							.catch(() => false);
					}
					return animation.finished.catch(() => false);
				}),
		);

		animationPromises.then(this.#handleTransitionEnd).catch(() => false);
	};

	#handleClick = (event: Event): void => {
		event.preventDefault();
		this.toggle();
	};

	#handleClickOutside = (event: Event): void => {
		if (!this.#isOpen || this.contains(event.target as Element)) {
			return;
		}
		this.toggle(false);
	};

	#handleKeyDown = (event: KeyboardEvent): void => {
		if (event.key === "Escape") {
			this.toggle(false);
		}
	};

	#handleTransitionEnd = (event: (boolean | Animation)[]): void => {
		if (event.find((transition) => !transition) !== false) {
			if (this.#useHeight && !this.#isOpen) {
				this.#content.style.height = "";
				this.#content.style.overflow = "";
			}

			if (this.#isOpen === false) {
				this.removeAttribute("open");
				this.removeAttribute("closing");
				this.dispatchEvent(this.closeEnd);
			} else {
				this.dispatchEvent(this.openEnd);
			}
		}
	};

	#destroy = () => {
		this.#summary?.removeEventListener("click", this.#handleClick);
		document.removeEventListener("click", this.#handleClickOutside);
	};

	#init = () => {
		this.#summary = this.querySelector(":scope > summary")!;
		this.#content = this.querySelector(":scope > *:not(summary)")!;
		if (!this.#summary || !this.#content) {
			return;
		}
		this.#isOpen = this.getAttribute("open") !== null;
		this.#isDropdown = this.dataset.detailsDropdown !== undefined;
		this.openStart = new Event("openStart", { bubbles: true });
		this.openEnd = new Event("openEnd", { bubbles: true });
		this.closeStart = new Event("closeStart", { bubbles: true });
		this.closeEnd = new Event("closeEnd", { bubbles: true });
		this.#summary.addEventListener("click", this.#handleClick);

		const transitionPropery = window
			.getComputedStyle(this.#content)
			.transitionProperty.split(",")
			.map((prop) => prop.trim());

		if (transitionPropery.includes("height")) {
			this.#useHeight = true;
		}

		this.addEventListener("toggle", (event: Event) => {
			if ((event as ToggleEvent).newState === "open" && !this.#isOpen) {
				this.toggle(true);
			}
		});
	};

	connectedCallback(): void {
		this.#init();
	}

	disconnectedCallback(): void {
		this.#destroy();
	}
}

customElements.define(DetailsTransition.NAME, DetailsTransition, {
	extends: "details",
});
